import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';

const ShopifyOauth = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(true); // eslint-disable-line no-unused-vars
    const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
    const [message, setMessage] = useState('Processing OAuth callback...');
    const [error, setError] = useState('');

    useEffect(() => {
        processOAuthCallback();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const processOAuthCallback = async () => {
        try {
            setIsProcessing(true);
            setStatus('processing');
            setMessage('Validating OAuth callback...');

            // Extract query parameters
            const shop = searchParams.get('shop');
            const code = searchParams.get('code');
            const hmac = searchParams.get('hmac');
            const state = searchParams.get('state');
            const timestamp = searchParams.get('timestamp');
            const host = searchParams.get('host');

            // Validate required parameters
            if (!shop || !code || !hmac || !state || !timestamp) {
                throw new Error('Missing required OAuth parameters');
            }

            // Validate shop domain format
            if (!shop.endsWith('.myshopify.com')) {
                throw new Error('Invalid shop domain format');
            }

            // Validate state parameter (CSRF protection)
            // Skip validation for scope_update reinstall flow (backend validates via DynamoDB)
            if (!state.startsWith('scope_update_')) {
                const storedState = sessionStorage.getItem('shopify_oauth_state');
                if (!storedState || storedState !== state) {
                    throw new Error('Invalid state parameter - possible CSRF attack');
                }
            }

            setMessage('Exchanging authorization code for access token...');

            // Call your Lambda function to process the OAuth callback
            const lambdaUrl = 'https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/shopify/api/auth';

            // Construct query parameters for the Lambda function
            const queryParams = new URLSearchParams({
                shop,
                code,
                hmac,
                state,
                timestamp,
                ...(host && { host })
            });

            const fullUrl = `${lambdaUrl}?${queryParams.toString()}`;

            console.log('Calling Lambda function:', fullUrl);

            try {
                const response = await fetch(fullUrl, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                });

                console.log('Lambda response status:', response.status);

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
                }

                const result = await response.json();
                console.log('Lambda response:', result);

                if (result.success) {
                    setStatus('success');
                    setMessage('Successfully connected to Shopify!');

                    // Clean up stored OAuth state
                    sessionStorage.removeItem('shopify_oauth_state');
                    sessionStorage.removeItem('shopify_shop_domain');

                    // Store shop information for the app using ShopifyAuth keys
                    const { ShopifyAuth } = await import('../utils/shopifyAuth');

                    // Debug: Log what we received
                    console.log('OAuth response:', result);
                    console.log('Auth token from response:', result.auth_token);

                    if (!result.auth_token) {
                        console.error('No auth_token in OAuth response!');
                        setStatus('error');
                        setError('No authentication token received from server');
                        return;
                    }

                    ShopifyAuth.setAuthData(
                        result.auth_token,
                        shop,
                        host,
                        'offline'
                    );

                    // Redirect to app dashboard after a short delay
                    setTimeout(() => {
                        const appUrl = `/shopify/${shop.replace('.myshopify.com', '')}/dashboard`;
                        navigate(appUrl, { replace: true });
                    }, 2000);
                } else {
                    throw new Error(result.error || 'OAuth processing failed on server');
                }

            } catch (fetchError) {
                console.error('Lambda function call failed:', fetchError);
                throw new Error(`Failed to process OAuth: ${fetchError.message}`);
            }

        } catch (err) {
            console.error('OAuth callback error:', err);
            setStatus('error');
            setError(err.message);
            setMessage('Failed to complete OAuth flow');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRetry = () => {
        // Clear stored state and redirect back to installation
        sessionStorage.removeItem('shopify_oauth_state');
        sessionStorage.removeItem('shopify_shop_domain');
        navigate('/shopify/app', { replace: true });
    };

    const renderProcessingState = () => (
        <div className="text-center">
            <Spinner animation="border" variant="success" style={{ width: '3rem', height: '3rem' }} />
            <h4 className="mt-3 text-success">Connecting to Shopify</h4>
            <p className="text-muted">{message}</p>
            <div className="mt-4">
                <div className="progress" style={{ height: '4px' }}>
                    <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                        style={{ width: '75%' }}
                    ></div>
                </div>
            </div>
        </div>
    );

    const renderSuccessState = () => (
        <div className="text-center">
            <div className="mb-4">
                <i className="fas fa-check-circle fa-5x text-success"></i>
            </div>
            <h3 className="text-success mb-3">Connection Successful!</h3>
            <p className="text-muted mb-4">{message}</p>
            <div className="alert alert-success">
                <strong>What's happening now:</strong>
                <ul className="list-unstyled mt-2 mb-0">
                    <li>✅ OAuth authentication completed</li>
                    <li>✅ Access token secured</li>
                    <li>✅ Webhooks registered</li>
                    <li>🔄 Redirecting to your app...</li>
                </ul>
            </div>
        </div>
    );

    const renderErrorState = () => (
        <div className="text-center">
            <div className="mb-4">
                <i className="fas fa-exclamation-triangle fa-5x text-danger"></i>
            </div>
            <h3 className="text-danger mb-3">Connection Failed</h3>
            <Alert variant="danger">
                <Alert.Heading>OAuth Error</Alert.Heading>
                <p className="mb-0">{error}</p>
            </Alert>
            <div className="mt-4">
                <button
                    className="btn btn-primary me-3"
                    onClick={handleRetry}
                >
                    Try Again
                </button>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/shopify')}
                >
                    Back to Landing
                </button>
            </div>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8} xl={6}>
                        <Card className="shadow-lg border-0">
                            <Card.Header className="bg-success text-white text-center py-4">
                                <h2 className="mb-0">
                                    <i className="fab fa-shopify me-2"></i>
                                    ALANI360 × Shopify
                                </h2>
                                <small>OAuth Authentication</small>
                            </Card.Header>
                            <Card.Body className="p-5">
                                {status === 'processing' && renderProcessingState()}
                                {status === 'success' && renderSuccessState()}
                                {status === 'error' && renderErrorState()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Additional Information */}
                <Row className="justify-content-center mt-4">
                    <Col lg={8} xl={6}>
                        <Card className="bg-light border-0">
                            <Card.Body className="text-center py-3">
                                <small className="text-muted">
                                    <i className="fas fa-shield-alt me-1"></i>
                                    Secure OAuth 2.0 authentication powered by Shopify
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ShopifyOauth;